import React, { useCallback, useState } from 'react';

import { useIntl } from 'react-intl';

import { UI_EVENT_TYPE } from '@atlaskit/analytics-gas-types';
import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next/types';
import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import IconButton from '@atlaskit/button/icon/button';
import AddIcon from '@atlaskit/icon/core/add';
import ButtonItem from '@atlaskit/menu/button-item';
import MenuGroup from '@atlaskit/menu/menu-group';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { Popup } from '@atlaskit/popup/popup';

import { ANALYTICS_CHANNEL } from '../../constants';
import type { GlobalShortcutActionsItem } from '../../types';
import { messages } from './messages';

type ShortcutActionProps = {
	actions: GlobalShortcutActionsItem[];
	analyticsName?: string;
};

type ButtonItemWithAnalyticsProps = {
	action: GlobalShortcutActionsItem;
	fireShortcutActionDropdownItemClickedAnalyticsEvent: (itemId?: string) => void;
	index: number;
	setIsOpen: (open: boolean) => void;
};

export const ShortcutActionMenu = ({
	actions,
	analyticsName,
}: ShortcutActionProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [isOpen, setIsOpen] = useState(false);

	const handleClick = useCallback(() => {
		setIsOpen(true);
		fireShortcutActionMenuClickedAnalyticsEvent(createAnalyticsEvent, analyticsName);
	}, [createAnalyticsEvent, analyticsName, setIsOpen]);

	const handleDropdownItemClick = useCallback(
		(itemId?: string) => {
			fireShortcutActionDropdownItemClickedAnalyticsEvent(createAnalyticsEvent, itemId);
		},
		[createAnalyticsEvent],
	);

	return (
		<Popup
			shouldRenderToParent
			isOpen={isOpen}
			onClose={() => setIsOpen(false)}
			placement="bottom-start"
			content={() => (
				<MenuGroup>
					{actions.map((action, index) => (
						<ButtonItemWithAnalytics
							index={index}
							action={action}
							fireShortcutActionDropdownItemClickedAnalyticsEvent={handleDropdownItemClick}
							setIsOpen={setIsOpen}
						/>
					))}
				</MenuGroup>
			)}
			trigger={(triggerProps) => (
				<IconButton
					{...triggerProps}
					appearance="subtle"
					spacing="compact"
					icon={(props) => <AddIcon {...props} size="small" />}
					label={formatMessage(messages.shortcutActionOpenMenuLabel)}
					onClick={handleClick}
				/>
			)}
		/>
	);
};

const ButtonItemWithAnalytics = ({
	index,
	action,
	fireShortcutActionDropdownItemClickedAnalyticsEvent,
	setIsOpen,
}: ButtonItemWithAnalyticsProps) => {
	const handleClick = useCallback(
		(e: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
			e.preventDefault();
			action.onClick?.();
			if (fg('navx-global-side-nav-confluence')) {
				fireShortcutActionDropdownItemClickedAnalyticsEvent(action.itemId);
			}
			setIsOpen(false);
		},
		[action, fireShortcutActionDropdownItemClickedAnalyticsEvent, setIsOpen],
	);

	return (
		<ButtonItem key={index} onClick={handleClick}>
			{action.component}
		</ButtonItem>
	);
};

const fireShortcutActionMenuClickedAnalyticsEvent = (
	createAnalyticsEvent: CreateUIAnalyticsEvent,
	analyticsName?: string,
) => {
	createAnalyticsEvent({
		eventType: UI_EVENT_TYPE,
		action: 'clicked',
		actionSubject: 'button',
		actionSubjectId: 'ShortcutActionMenu',
		attributes: {
			name: analyticsName,
		},
	}).fire(ANALYTICS_CHANNEL);
};

const fireShortcutActionDropdownItemClickedAnalyticsEvent = (
	createAnalyticsEvent: CreateUIAnalyticsEvent,
	itemId?: string,
) => {
	createAnalyticsEvent({
		eventType: UI_EVENT_TYPE,
		action: 'clicked',
		actionSubject: 'button',
		actionSubjectId: 'ShortcutActionDropdownItem',
		attributes: {
			itemId: itemId,
		},
	}).fire(ANALYTICS_CHANNEL);
};
